/**
 * readEffectiveAccounts — RPC contract for the callable that answers which
 * provider accounts an organization's mail and checkout run on right now
 * (firebase/src/settings/readEffectiveAccounts.api.ts). It is computed by
 * the same resolutions the sends and checkout use — resolveOrganizationSender
 * and resolveActiveModeAndCurrency — so Settings and onboarding say what the
 * server will actually do rather than re-deriving it from stored stamps.
 * Read-only; no credential value ever leaves it.
 */
import { z } from "zod";
import { PaymentModeSchema, SendingAccountSchema } from "./order.entity.ts";
import { Id } from "./workspace.entity.ts";

export const ReadEffectiveAccountsInput = z.object({ orgId: Id }).strict();
export type ReadEffectiveAccountsInput = z.infer<
	typeof ReadEffectiveAccountsInput
>;

// Fields justified — each is a line the organization's screens state:
//   mail.account    — whose account its mail goes out on; null when neither
//                     can send (the sends refuse).
//   mail.sender     — the From address that account sends from — the
//                     deployment's sender on the platform account, the
//                     card's on its own; null with no account.
//   payments.mode   — the environment a new checkout opens in, the activated
//                     one or the deployment's sandbox fallback; null when
//                     checkout is closed.
//   payments.platformSandbox — whether that sandbox runs on the deployment's
//                     key rather than one of the organization's own.
export const ReadEffectiveAccountsOutput = z
	.object({
		mail: z
			.object({
				account: SendingAccountSchema.nullable(),
				sender: z.string().email().nullable(),
			})
			.strict(),
		payments: z
			.object({
				mode: PaymentModeSchema.nullable(),
				platformSandbox: z.boolean(),
			})
			.strict(),
	})
	.strict();
export type ReadEffectiveAccountsOutput = z.infer<
	typeof ReadEffectiveAccountsOutput
>;
